import React, { useRef } from 'react';
import '../graphStyles.scss';
import NoDataFound from "../../NoDataFound";
import DateRangeFilter from "../DateRangeFilter/DateRangeFilter";
import useDateRangeFilter from "../DateRangeFilter/useDateRangeFilter";
import { ExportDropdown } from "../../ExportDropdown";
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  BarController,
  LineController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

const InfoIcon = ({ tooltipText }) => (
  <span className="info-tooltip-container">
    <svg className="info-tooltip-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
    <span className="info-tooltip-bubble">{tooltipText}</span>
  </span>
);

ChartJS.register(
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  BarElement,
  LineController,
  BarController
);

export function ShopperOriginsGraph({ google, facebook, linkedin, instagram, direct, linktree, reddit, others, pinterest, hubspot, microsoft, afmc, weeks }) {
  const chartRef = useRef(null);

  const { filteredWeeks, filteredData, onFilterChange } = useDateRangeFilter({
    weeks,
    dataArrays: { google, facebook, linkedin, instagram, direct, linktree, reddit, others, pinterest, hubspot, microsoft, afmc },
  });

  const [legendClicked, setLegendClicked] = React.useState(false);

  function legendClickHandler(e, legendItem) {
    let datasetIndex = legendItem.datasetIndex;
    let ci = this.chart, metaSets = [];

    if (!legendClicked) {
      for (let i = 0; i < ci.data.datasets.length; i++) {
        metaSets.push(ci.getDatasetMeta(i));
      }
      metaSets.forEach(function (meta) {
        meta.hidden = meta.controller.index === datasetIndex ? false : true;
      });
      ci.update();
    } else {
      for (let i = 0; i < ci.data.datasets.length; i++) {
        metaSets.push(ci.getDatasetMeta(i));
      }
      metaSets.forEach(function (meta) {
        meta.hidden = meta.controller.index === datasetIndex ? false : false;
      });
      ci.update();
    }
    setLegendClicked(prev => !prev);
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        onClick: legendClickHandler,
        position: 'right',
        labels: {
          usePointStyle: true,
          useBorderRadius: true,
          padding: function () {
            if (window.innerWidth < 600) {
              return 6;
            } else if (window.innerWidth < 650) {
              return 7;
            } else if (window.innerWidth < 700) {
              return 8;
            } else if (window.innerWidth < 750) {
              return 9;
            } else if (window.innerWidth < 800) {
              return 10;
            } else if (window.innerWidth < 900) {
              return 10;
            } else if (window.innerWidth < 1100) {
              return 13;
            } else if (window.innerWidth < 1300) {
              return 16;
            } else {
              return 18;
            }
          },
          font: {
            size: function () {
              if (window.innerWidth < 600) {
                return 10;
              } else if (window.innerWidth < 700) {
                return 12;
              } else if (window.innerWidth < 800) {
                return 13;
              } else if (window.innerWidth < 900) {
                return 13.5;
              } else if (window.innerWidth < 1100) {
                return 14.5;
              } else if (window.innerWidth < 1300) {
                return 16;
              } else if (window.innerWidth < 1450) {
                return 17;
              } else if (window.innerWidth < 1600) {
                return 18;
              } else {
                return 19;
              }
            },
          },
        },
        onHover: function (event, legendItem) {
          document.getElementById("chart22").style.cursor = 'pointer';
        },
        onLeave: function (event, legendItem) {
          document.getElementById("chart22").style.cursor = 'default';
        },
      },
    },
    scales: {
      x: {
        title: {
          text: "Week",
          display: true,
          font: {
            size: 13.5,
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            margin: -5,
          },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Number of shoppers',
          font: {
            size: 13.5,
          },
        },
      },
    },
  };

  const labels = filteredWeeks;

  const data = {
    labels,
    datasets: [
      {
        label: 'Google',
        type: 'line',
        data: filteredData.google,
        borderColor: 'rgba(190, 70, 160, 0.6)',
        backgroundColor: 'rgba(230, 80, 140, 1)',
        hoverBackgroundColor: 'rgba(230, 80, 140, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'Facebook',
        type: 'line',
        data: filteredData.facebook,
        borderColor: 'rgba(32, 114, 255, 0.7)',
        backgroundColor: 'rgba(32, 114, 255, 1)',
        hoverBackgroundColor: 'rgba(32, 114, 255, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'LinkedIn',
        type: 'line',
        data: filteredData.linkedin,
        borderColor: 'rgba(240, 200, 50, 0.7)',
        backgroundColor: 'rgba(240, 200, 50, 1)',
        hoverBackgroundColor: 'rgba(240, 200, 50, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'Instagram',
        type: 'line',
        data: filteredData.instagram,
        borderColor: 'rgba(250, 170, 200, 0.7)',
        backgroundColor: 'rgba(250, 170, 200, 1)',
        hoverBackgroundColor: 'rgba(250, 170, 200, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'Direct',
        type: 'line',
        data: filteredData.direct,
        borderColor: 'rgba(0, 240, 110, 0.6)',
        backgroundColor: 'rgba(0, 210, 0, 1)',
        hoverBackgroundColor: 'rgba(0, 210, 0, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'Linktree',
        type: 'line',
        data: filteredData.linktree,
        borderColor: 'rgba(200, 0, 0, 0.7)',
        backgroundColor: 'rgba(200, 0, 0, 1)',
        hoverBackgroundColor: 'rgba(200, 0, 0, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'Reddit',
        type: 'line',
        data: filteredData.reddit,
        borderColor: 'rgba(0, 230, 220, 0.7)',
        backgroundColor: 'rgba(0, 210, 200, 1)',
        hoverBackgroundColor: 'rgba(0, 210, 200, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'Others',
        type: 'line',
        data: filteredData.others,
        borderColor: 'rgba(100, 0, 180, 1)',
        backgroundColor: 'rgba(190, 0, 240, 0.7)',
        hoverBackgroundColor: 'rgba(190, 0, 240, 0.7)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'Microsoft',
        type: 'line',
        data: filteredData.microsoft,
        borderColor: 'rgba(0, 180, 50, 0.5)',
        backgroundColor: 'rgba(0, 120, 50, 1)',
        hoverBackgroundColor: 'rgba(0, 120, 50, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'HubSpot',
        type: 'line',
        data: filteredData.hubspot,
        borderColor: 'rgba(100, 150, 0, 0.5)',
        backgroundColor: 'rgba(130, 180, 0, 1)',
        hoverBackgroundColor: 'rgba(130, 180, 0, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'Pinterest',
        type: 'line',
        data: filteredData.pinterest,
        borderColor: 'rgba(250, 140, 0, 0.5)',
        backgroundColor: 'rgba(230, 120, 0, 1)',
        hoverBackgroundColor: 'rgba(230, 120, 0, 1)',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
      {
        label: 'Affiliate Marketing (afmc)',
        type: 'line',
        data: filteredData.afmc,
        borderColor: 'rgba(18, 28, 203, 0.5)',
        backgroundColor: '#244dc7',
        hoverBackgroundColor: '#244dc7',
        lineTension: 0.2,
        pointRadius: function () {
          return window.innerWidth < 600 ? 2 : 3;
        },
      },
    ],
  };

  const allData = [
    ["Origin", ...filteredWeeks],
    ["Google", ...filteredData.google],
    ["Facebook", ...filteredData.facebook],
    ["LinkedIn", ...filteredData.linkedin],
    ["Instagram", ...filteredData.instagram],
    ["Direct", ...filteredData.direct],
    ["Linktree", ...filteredData.linktree],
    ["Reddit", ...filteredData.reddit],
    ["Others", ...filteredData.others],
    ["Microsoft", ...filteredData.microsoft],
    ["HubSpot", ...filteredData.hubspot],
    ["Pinterest", ...filteredData.pinterest],
    ["Affiliate Marketing (afmc)", ...filteredData.afmc],
  ];

  return (
    <div className="graph">
      <div className="graph__title flex flex-col md:flex-row justify-between items-center">
        <h4 className="text-lg md:text-xl font-semibold text-center md:text-left mb-2 md:mb-0 flex items-center gap-1.5 justify-center md:justify-start">
          <span>Where are the shoppers coming from?</span>
          <InfoIcon tooltipText="Tracks the count of shoppers originating from different referral sources and marketing channels over time." />
        </h4>
        <div className="flex items-center gap-2 flex-wrap justify-end pr-3">
          <DateRangeFilter weeks={weeks} onFilterChange={onFilterChange} />
          <ExportDropdown
            data={allData}
            chartRef={chartRef}
            name="Where are the shoppers coming from"
          />
        </div>
      </div>
      <div className="relative w-full h-64 md:h-96">
        <Chart ref={chartRef} className="chart" id="chart22" options={options} data={data} />
      </div>
    </div>
  );
}
